<template>
    <header
        class="backdrop-blur-lg top-0 z-[9998] xl:h-16 xl:py-0 py-4 flex flex-col justify-center w-full"
        :class="showMobileMenu ? 'bg-black fixed' : 'bg-black/60 sticky h-16'"
    >
        <div class="container xl:px-0 px-6">
            <div class="flex items-center">
                <button v-if="useDevice().isMobileOrTablet" @click="toggleMobileMenu" aria-label="hamburger menu">
                    <Icon
                        :name="showMobileMenu ? 'tabler:x' : 'tabler:menu-2'"
                        size="24px"
                        class="text-gray-200 mr-1"
                    />
                </button>

                <div class="cursor-pointer" @click="navigateLink">
                    <AppLogo
                        v-if="
                            (!userData.restricted_user &&
                                !userData.free_plan &&
                                !userData.can_display_renewal_failed_banner) ||
                            useDevice().isDesktopOrTablet
                        "
                        :width="useDevice().isDesktop ? '135' : '98'"
                        class="xl:min-w-36 mr-4"
                    />
                </div>

                <!-- * desktop items and nav -->
                <template v-if="useDevice().isDesktop">
                    <ClientOnly>
                        <template #default>
                            <AppHeaderNavigation :items="headerNavigation" />
                        </template>

                        <template #fallback>
                            <div class="lg:flex lg:justify-between hidden flex-row items-center space-x-8 pl-7">
                                <NuxtLinkLocale
                                    :key="link.link"
                                    active-class="!text-gray-100"
                                    v-for="link in headerNavigation"
                                    class="text-gray-500 hover:text-gray-100 transition-all text-body-md pr-4"
                                    :to="link?.link"
                                >
                                    {{ link.title }}
                                </NuxtLinkLocale>
                            </div>
                        </template>
                    </ClientOnly>

                    <AppHeaderAuthButtons />
                </template>

                <AppHeaderAuthButtons v-if="useDevice().isMobile" />
            </div>

            <!-- * mobile menu -->
            <HeadlessTransitionRoot :show="showMobileMenu" v-if="useDevice().isMobileOrTablet" appear>
                <AppTransitionsAppXSlideTransition direction="left-to-right">
                    <div class="h-[calc(100vh-48px)] overflow-y-auto flex flex-col pt-5">
                        <AppMobileHeaderNavigation :items="headerNavigation" />
                    </div>
                </AppTransitionsAppXSlideTransition>
            </HeadlessTransitionRoot>
        </div>
    </header>
    <!-- * all banners should be here -->
    <div class="sticky top-16 z-[100]">
        <AppOfferBanner
            v-if="showMarketingBanner && !showRenewalBanner"
            @click="offerBannerClickHandler"
            :message="offerBannerMessageText"
            :link="t('components_offerBanner_link')"
            class="flex justify-center items-center"
        />

        <AppAutoRenewalBanner
            v-if="userData.can_display_renewal_failed_banner && !useRoute().fullPath.includes('dashboard')"
        />

        <AppPromotionalModal
            :has-gradient-button="true"
            shadow-color="bg-brand-500"
            :promotional-data="promotionalModalData"
            v-model:showPromotionModal="showPromotionModal"
        />
        <MiamiBanner v-show="showMiamiBanner" />
    </div>
</template>

<script setup lang="ts">
import { useHeaderData } from "~/data/header";
import { useUserStore } from "~/store/global/user.store";
import { useAuthStore } from "~/features/authentication/store";
const { showPromotionModal } = useDownloadPhoto();

const { userData, isMiamiRealtor } = storeToRefs(useUserStore());
const { headerNavigation, promotionalModalData } = useHeaderData();
const auth = useAuth();
const { t } = useI18n();
const showMobileMenu = useMobileMenuOpen();
const showModal = ref(false);
const emit = defineEmits(["toggle:mobileMenu"]);
const offerBannerMessageText = ref(t("components_offerBanner_text"));
const toggleMobileMenu = () => {
    showMobileMenu.value = !showMobileMenu.value;
    emit("toggle:mobileMenu", showMobileMenu.value);
};

const showRenewalBanner = computed(() => {
    const { free_plan, remaining_time, renewal_canceled } = userData.value;
    if (free_plan) return false;
    if (renewal_canceled) return false;
    if (remaining_time <= 0) return true;
});
const showMarketingBanner = computed(() => {
    if (auth.value) {
        const { free_plan, balance, remaining_time } = userData?.value;
        if (free_plan) return true;
        if (balance < 6 && remaining_time > 0) return true;
        if (balance > 6) return false;
    }
    return true;
});

const { activeComponent, showAuthModal } = storeToRefs(useAuthStore());

const openGlobalDialog = () => {
    activeComponent.value = "signup";
    showAuthModal.value = true;
};

const localePath = useLocalePath();
const offerBannerClickHandler = () => {
    if (!auth.value) {
        openGlobalDialog();
    } else {
        useRouter().push(localePath("/pricing"));
    }
};

const { navigateLink } = useHandleLogoNavigation();
const { isDesktop, isTablet } = useDevice();

const offerBannerMessage = () => {
    if (auth.value) {
        offerBannerMessageText.value = t("components_offerBanner_text_authenticated");
    } else {
        offerBannerMessageText.value = t("components_offerBanner_text-A/B-test");
    }
};

watch(auth, () => {
    offerBannerMessage();
});

const route = useRoute();
const showMiamiBanner = ref(false);
const miamiRealtorCookie = useCookie("is_miami_realtor");
const isCorrectPageContext = computed(() => route.path !== "/miamirealtors" && !route.fullPath.includes("dashboard"));
function handleShowMiamiBanner() {
    if (!miamiRealtorCookie.value) {
        showMiamiBanner.value = false;
        return;
    }
    if (!isCorrectPageContext.value) {
        showMiamiBanner.value = false;
        return;
    }
    if (miamiRealtorCookie.value && !auth.value) {
        showMiamiBanner.value = true;
        return;
    }
    if (miamiRealtorCookie.value && auth.value && userData.value && userData.value?.free_plan) {
        showMiamiBanner.value = true;
    } else {
        showMiamiBanner.value = false;
    }
}

watchEffect(() => {
    handleShowMiamiBanner();
});

onMounted(() => {
    offerBannerMessage();
});
</script>
